import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import moment from 'moment';

import checkIfCurrentTimeSlot from 'bl/scheduler/checkIfCurrentTimeSlot.bl';
import timeInRangeSimple from 'bl/scheduler/timeInRangeSimple.bl';
import FLAG from 'config/flag.singleton';
import { QF_TIMELINE_OFFSET, QF_TIMELINE_ZONE } from 'config/quickfix.singleton';
import DELAY from 'constants/delay.const';
import { useTheme } from 'providers/Theme.provider';
import asArray from 'utils/arrays/asArray.util';
import { fromAnyDateToIso } from 'utils/converters/time/from.util';
import { formatHourAndMinuteShort, formatPartAmPm } from 'utils/date/format.util';
import { timeAddMinutes } from 'utils/date/manipulate.util';
import { nowAsIso } from 'utils/date/now.util';
import zonedMoment from 'utils/date/zonedMoment.util';
import delayed from 'utils/functions/delayed.util';
import logger from 'utils/logger.util';
import { isTimeSameDay } from 'utils/predicates/time/compare.util';

import CN from './TimesColumn.module.scss';

const L = logger('TimesColumn'); // eslint-disable-line no-unused-vars

const SLOT_MINUTES_QUARTER = 15;
const SLOT_MINUTES_HALF = 30;

export const MIN_PIXELS = 0;
export const SPAN_MINUTES = 15;
export const SPAN_PIXELS = 24;
export const PIXELS_PER_MINUTE = SPAN_PIXELS / SPAN_MINUTES;

export const fromSlotMinuteToTopPixels = (minute) => {
  const isValid = Number.isSafeInteger(minute);
  if (!isValid) L.warn('fromSlotMinuteToTopPixels()', 'Invalid minute:', minute);
  const m = isValid ? minute : 0;
  return MIN_PIXELS + (m % SPAN_MINUTES) * PIXELS_PER_MINUTE;
};

const useTick = () => {
  const [current, setCurrent] = useState(nowAsIso());

  useEffect(() => {
    let finished = false;
    if (finished) return;

    const tick = () => {
      if (finished) return;

      const now = nowAsIso();
      if (FLAG.loggerSpam) L.debug('useTick()', 'now:', now);
      setCurrent(now);

      delayed(DELAY.autohide, tick);
    };

    tick();

    return () => void (finished = true);
  }, []);

  return current;
};

const fromSlotToFixedSlot = (slot) =>
  QF_TIMELINE_OFFSET
    ? timeAddMinutes(QF_TIMELINE_OFFSET, slot)
    : QF_TIMELINE_ZONE
      ? zonedMoment(QF_TIMELINE_ZONE, slot)
      : slot;

export const determineNow = (options) =>
  options?.isDay
    ? isTimeSameDay(options?.selectedDate, options?.current) && timeInRangeSimple(options)
    : checkIfCurrentTimeSlot(options);

const RowLabel = ({ currentTimeRef, current, slot, tz, selectedDate, isDay }) => {
  const { isDarkMode } = useTheme();

  const isNow = determineNow({ isDay, selectedDate, current, slot, tz, length: SPAN_MINUTES });

  const fixedSlot = fromSlotToFixedSlot(slot);

  const displayTime = formatHourAndMinuteShort(fixedSlot);
  const displayAmPm = formatPartAmPm(fixedSlot);
  const slotMinute = moment(fixedSlot).get('minute');
  const currentMinute = moment(current).get('minute');

  const top = `${String(fromSlotMinuteToTopPixels(currentMinute))}px`;

  return (
    <div
      data-bem="RowLabel"
      data-slot={fromAnyDateToIso(slot)}
      data-is-day={String(isDay)}
      data-is-now={String(isNow)}
      ref={isNow ? currentTimeRef : undefined}
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
        [CN.isNow]: isNow,
      })}
    >
      {slotMinute < SLOT_MINUTES_QUARTER ? (
        <div data-bem="RowLabel__slot-single--full" data-is-day={String(isDay)} className={CN.fullHour}>
          {displayTime}
          <small>{displayAmPm}</small>
        </div>
      ) : (
        <div data-bem="RowLabel__slot-single--empty" className="empty-line" />
      )}

      {slotMinute === SLOT_MINUTES_HALF ? (
        <div
          data-bem="RowLabel__slot-double--full"
          className={cn({
            [CN.halfHour]: true,
            half: true,
          })}
        >
          {displayTime}
          <small>{displayAmPm}</small>
        </div>
      ) : (
        <div data-bem="RowLabel__slot-double--empty" className="empty-line 30" />
      )}

      {isNow && (
        <div
          data-bem="RowLabel__line-container"
          data-top={top}
          className={cn({
            'red-line': true,
            [CN.redLineContainer]: true,
          })}
        >
          <div data-bem="RowLabel__line-animate" data-top={top} className="animate-line" style={{ top }}>
            <span data-bem="RowLabel__line-time">{formatHourAndMinuteShort(current)} </span>
          </div>
        </div>
      )}
    </div>
  );
};

const TimesColumn = ({ currentTimeRef, range, tz, selectedDate, isDay, extraClass }) => {
  const { isDarkMode } = useTheme();
  const current = useTick();

  return (
    <div
      data-bem="TimesColumn"
      data-is-day={String(isDay)}
      data-current={String(current)}
      data-selected={String(selectedDate)}
      className={cn({
        [CN.hoursColumn]: true,
        [CN.dark]: isDarkMode,
        [CN.moveDown]: extraClass,
      })}
    >
      {asArray(range).map((slot) => (
        <RowLabel
          key={fromAnyDateToIso(slot)}
          currentTimeRef={currentTimeRef}
          slot={slot}
          current={current}
          tz={tz}
          selectedDate={selectedDate}
          isDay={isDay}
        />
      ))}
    </div>
  );
};

export default TimesColumn;
